/**
 * Köttbutiken: Kollektioner: lista (sections/kb-kollektioner-lista.liquid).
 * Varje rad (blocks/_kb-kollektionsrad.liquid) renderar sin stora bild; skriptet flyttar bilderna till bildytan
 * bredvid listan och visar raden man håller över eller tabbar till. Bilden står kvar när pekaren lämnar listan,
 * så att den inte blinkar mellan raderna. När temaredigeraren ritar om ett block flyttas den nya bilden in och
 * den gamla tas bort, och bilden följer valt block.
 */
class KbKollektionslista extends HTMLElement {
  #avbryt = new AbortController();
  /** @type {MutationObserver | undefined} */
  #observator;

  connectedCallback() {
    const { signal } = this.#avbryt;
    this.addEventListener('pointerover', this.#vidRad, { signal });
    this.addEventListener('focusin', this.#vidRad, { signal });
    document.addEventListener('shopify:block:select', this.#vidBlockval, { signal });

    this.#synka();
    const lista = this.querySelector('.kb-kl__lista');
    if (lista) {
      this.#observator = new MutationObserver(() => this.#synka());
      this.#observator.observe(lista, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    this.#avbryt.abort();
    this.#observator?.disconnect();
  }

  get #ram() {
    return this.querySelector('.kb-kl__bildram');
  }

  #synka() {
    const ram = this.#ram;
    if (!ram) return;
    const aktiv = ram.querySelector('.kb-kl__bild--aktiv')?.getAttribute('data-kb-bild');
    const rader = [...this.querySelectorAll('.kb-kl__rad[data-kb-rad]')];
    const ids = new Set(rader.map((rad) => rad.getAttribute('data-kb-rad')));

    for (const rad of rader) {
      const bild = rad.querySelector(':scope > .kb-kl__bild');
      if (!bild) continue;
      ram.querySelector(`[data-kb-bild="${bild.getAttribute('data-kb-bild')}"]`)?.remove();
      ram.append(bild);
    }
    for (const bild of ram.querySelectorAll('[data-kb-bild]')) {
      if (!ids.has(bild.getAttribute('data-kb-bild'))) bild.remove();
    }

    // Ordningen i ytan följer listan (spelar bara roll för vilken som är först)
    for (const rad of rader) {
      const bild = ram.querySelector(`[data-kb-bild="${rad.getAttribute('data-kb-rad')}"]`);
      if (bild) ram.append(bild);
    }

    const forsta = rader[0]?.getAttribute('data-kb-rad');
    const visa = aktiv && ids.has(aktiv) ? aktiv : forsta;
    if (visa) this.#visa(visa);
  }

  /** @param {Event} event */
  #vidRad = (event) => {
    const rad = event.target instanceof Element ? event.target.closest('[data-kb-rad]') : null;
    const id = rad?.getAttribute('data-kb-rad');
    if (id) this.#visa(id);
  };

  /** @param {any} event */
  #vidBlockval = (event) => {
    const id = event.detail?.blockId;
    if (!id) return;
    const rad = [...this.querySelectorAll('[data-kb-rad]')].find((r) => r.getAttribute('data-kb-rad')?.endsWith(id));
    const radId = rad?.getAttribute('data-kb-rad');
    if (radId) this.#visa(radId);
  };

  /** @param {string} id */
  #visa(id) {
    const ram = this.#ram;
    const bild = ram?.querySelector(`[data-kb-bild="${id}"]`);
    if (!ram || !bild) return;
    for (const annan of ram.querySelectorAll('.kb-kl__bild--aktiv')) {
      if (annan !== bild) annan.classList.remove('kb-kl__bild--aktiv');
    }
    bild.classList.add('kb-kl__bild--aktiv');
  }
}

if (!customElements.get('kb-kollektionslista')) {
  customElements.define('kb-kollektionslista', KbKollektionslista);
}
